// /Users/charispetsas/Code/GEOIMAGING/MSP4Growth/frontend/msp/src/components/map/LayerControl.js
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import ReactDOM from 'react-dom/client';
import { Box, FormControlLabel, Checkbox, Typography, Accordion, AccordionSummary, AccordionDetails, Tooltip, IconButton, Switch, Divider } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';


const LayerControlUI = ({ layers = [], layerVisibility, onVisibilityChange, transparencyMode, onTransparencyChange }) => {
    const selectedLayer = Object.keys(layerVisibility).find(id => layerVisibility[id]) || '';

    return (
        <Box sx={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            padding: '16px',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.25)',
            minWidth: '280px',
            maxWidth: '320px'
        }}>
            <Typography 
                variant="h6" 
                sx={{ 
                    marginBottom: '12px', 
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: '1.1rem'
                }}
            >
                Landslide Analysis Toolbox
            </Typography>
            {layers.map(([groupName, groupLayers]) => (
                <Accordion 
                    key={groupName} 
                    defaultExpanded
                    sx={{
                        background: 'rgba(255, 255, 255, 0.6)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '12px !important',
                        mb: 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        '&:before': {
                            display: 'none',
                        },
                        '&.Mui-expanded': {
                            margin: '0 0 8px 0',
                        }
                    }}
                >
                    <AccordionSummary
                        expandIcon={'▼'}
                        sx={{ 
                            minHeight: '44px',
                            borderRadius: '12px',
                            '& .MuiAccordionSummary-content': { 
                                margin: '10px 0' 
                            },
                            '&:hover': {
                                background: 'rgba(33, 147, 176, 0.1)',
                            }
                        }}
                    > 
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 700,
                                color: '#2193b0',
                                fontSize: '0.95rem'
                            }}
                        >
                            {groupName}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ 
                        padding: '0 12px 12px',
                        maxHeight: '180px',
                        overflowY: 'auto',
                        pr: '8px',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: 'linear-gradient(135deg, #1a7a93 0%, #5bc3de 100%)',
                        }
                    }}>
                        {groupLayers.map(layer => (
                            <Box 
                                key={layer.id} 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    padding: '4px 8px',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        background: 'rgba(33, 147, 176, 0.1)',
                                    }
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!layerVisibility[layer.id]}
                                            onChange={(e) => onVisibilityChange(e.target.checked ? layer.id : '')}
                                            size="small"
                                            icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #2193b0' }} />}
                                            checkedIcon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#2193b0', border: '2px solid #2193b0' }} />}
                                            sx={{
                                                color: '#2193b0',
                                                '&.Mui-checked': {
                                                    color: '#2193b0',
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontSize: '0.875rem',
                                                color: '#1e293b'
                                            }}
                                        >
                                            {layer.name}
                                        </Typography>
                                    }
                                    sx={{ flexGrow: 1, margin: 0 }}
                                />
                                {layer.description && (
                                    <Tooltip title={layer.description} placement="left">
                                        <IconButton 
                                            size="small" 
                                            sx={{ 
                                                padding: '4px',
                                                '&:hover': {
                                                    background: 'rgba(33, 147, 176, 0.15)',
                                                }
                                            }}
                                        >
                                            <InfoIcon fontSize="small" sx={{ color: '#2193b0' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            ))}
            {selectedLayer && (
                <>
                    <Divider sx={{ 
                        my: 2,
                        borderColor: 'rgba(33, 147, 176, 0.3)'
                    }} />
                    <Box sx={{ 
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'rgba(255, 255, 255, 0.6)',
                        borderRadius: '10px',
                        backdropFilter: 'blur(5px)'
                    }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 600,
                                color: '#1e293b'
                            }}
                        >
                            Transparency Mode
                        </Typography>
                        <Switch 
                            checked={transparencyMode} 
                            onChange={(e) => onTransparencyChange(e.target.checked)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#2193b0',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#2193b0',
                                }
                            }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

const LayerControl = ({ layers = [], layerVisibility, onVisibilityChange, transparencyMode, onTransparencyChange }) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const control = L.control({ position: 'topright' }); 

        control.onAdd = () => {
            const div = L.DomUtil.create('div', 'leaflet-layer-control leaflet-control');
            L.DomEvent.disableClickPropagation(div);
            L.DomEvent.disableScrollPropagation(div);
            L.DomEvent.on(div, 'mousedown', L.DomEvent.stopPropagation);
            
            const root = ReactDOM.createRoot(div);
            root.render(
                <LayerControlUI
                    layers={layers}
                    layerVisibility={layerVisibility}
                    onVisibilityChange={onVisibilityChange}
                    transparencyMode={transparencyMode}
                    onTransparencyChange={onTransparencyChange}
                />
            );
            div.reactRoot = root;
            return div;
        };

        control.onRemove = () => {
            const div = control.getContainer();
            if (div && div.reactRoot) {
                queueMicrotask(() => {
                    div.reactRoot.unmount();
                    delete div.reactRoot;
                });
            }
        };

        control.addTo(map);

        return () => {
            if (map && control) {
                map.removeControl(control);
            }
        };
    }, [map, layers, layerVisibility, onVisibilityChange, transparencyMode, onTransparencyChange]);

    return null;
};

export default LayerControl;
